import { LoadingButton } from "@mui/lab";
import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useRef, useState } from "react";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CustomDialogTitle from "../../../components/Common/customDialogTitle";
import useAxiosInstance from "../../../hooks/useAxiosInstance";
import { API_RESULT, API_VERSION } from "../../../enums/common.enum";
import { errorToast, handleError, successToast } from "../../../utils/common";
import useUserInfo from "../../../hooks/useUserInfo";
import * as yup from "yup";
import { ownerSchema } from "../../../schemas/providers.schema";

type OwnerDetail = {
  owner_name: string;
  phone_number: string;
  owner_image_url: string;
  aadhaar_no: string;
  aadhaar_url: string;
  pan_no: string;
  pan_url: string;
};

const EditOwnerDetailsModal = ({
  showOwnerModal,
  setShowOwnerModal,
  borrowerInfo,
  setBorrowerInfo,
  type,
  selectedOwner,
  setSelectedOwner,
}: any) => {
  const privateInstance = useAxiosInstance();
  const user = useUserInfo();
  const isEdit = type === "edit";

  const ownerImgRef = useRef<HTMLInputElement | null>(null);
  const aadhaarRef = useRef<HTMLInputElement | null>(null);
  const panRef = useRef<HTMLInputElement | null>(null);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({
    owner: false,
    aadhaar: false,
    pan: false,
  });
  const [progress, setProgress] = useState({
    owner: 0,
    aadhaar: 0,
    pan: 0,
  });

  const uploadFile = async (
    file: File,
    key: "owner_image_url" | "aadhaar_url" | "pan_url",
    type: "owner" | "aadhaar" | "pan",
    setFieldValue: any,
  ) => {
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      errorToast("Max allowed file size is 10 MB");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploading((p) => ({ ...p, [type]: true }));
    setProgress((p) => ({ ...p, [type]: 0 }));

    try {
      const res = await privateInstance.post(
        `${API_VERSION.V1}/orders/reports/upload?created_by=${user?.user_id}&report_type=owner`,
        formData,
        {
          onUploadProgress: (e) => {
            if (e.total) {
              setProgress((p) => ({
                ...p,
                [type]: Math.round((e.loaded * 100) / e.total),
              }));
            }
          },
        },
      );

      if (res?.data?.status === API_RESULT.SUCCESS) {
        setFieldValue(key, res.data.response?.url);
        successToast("File uploaded successfully");
      } else {
        errorToast(res.data.message);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setUploading((p) => ({ ...p, [type]: false }));
    }
  };

  const handleSubmit = async (values: any) => {
    const updatedOwner: OwnerDetail = {
      owner_name: values.owner_name,
      phone_number: values.phone_number,
      owner_image_url: values.owner_image_url,
      aadhaar_no: values.aadhaar_no,
      aadhaar_url: values.aadhaar_url,
      pan_no: values.pan_no,
      pan_url: values.pan_url,
    };

    let updatedOwners: OwnerDetail[] = [];

    if (isEdit && selectedOwner) {
      updatedOwners = (borrowerInfo.owner_details || []).map(
        (owner: OwnerDetail) =>
          owner.aadhaar_no === selectedOwner.aadhaar_no ? updatedOwner : owner,
      );
    } else {
      updatedOwners = [...(borrowerInfo.owner_details || []), updatedOwner];
    }

    const payload = {
      borrower_id: borrowerInfo.borrower_id,
      owner_details: updatedOwners,
    };

    setLoading(true);

    try {
      const res = await privateInstance.post(
        `${API_VERSION.V3}/borrowers`,
        payload,
      );

      if (res?.data?.status === API_RESULT.SUCCESS) {
        setBorrowerInfo((prev: any) => ({
          ...prev,
          owner_details: updatedOwners,
        }));

        successToast(
          isEdit
            ? "Owner details updated successfully"
            : "Owner details added successfully",
        );

        setSelectedOwner(null);
        setShowOwnerModal(false);
      } else {
        errorToast(res.data.message);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={showOwnerModal}
      onClose={() => setShowOwnerModal(false)}
      fullWidth
      maxWidth="md"
    >
      <CustomDialogTitle
        id="customized-dialog-title"
        onClose={() => setShowOwnerModal(false)}
      >
        {isEdit ? "Update Owner Details" : "Add Owner Details"}
      </CustomDialogTitle>

      <DialogContent>
        <Formik
          initialValues={{
            owner_name: selectedOwner?.owner_name || "",
            phone_number: selectedOwner?.phone_number || "",
            owner_image_url: selectedOwner?.owner_image_url || "",
            aadhaar_no: selectedOwner?.aadhaar_no || "",
            aadhaar_url: selectedOwner?.aadhaar_url || "",
            pan_no: selectedOwner?.pan_no || "",
            pan_url: selectedOwner?.pan_url || "",
          }}
          validationSchema={ownerSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, errors, touched }) => (
            <Form>
              <Box textAlign="center" mb={3}>
                {!values.owner_image_url ? (
                  <Box
                    onClick={() => ownerImgRef.current?.click()}
                    sx={{
                      width: 120,
                      height: 120,
                      mx: "auto",
                      borderRadius: "50%",
                      border: "2px dashed #3f6ee8",
                      display: "flex",
                      flexDirection: "column", // 🔑
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      backgroundColor: "#fafbff",
                      gap: 0.5,
                    }}
                  >
                    {uploading.owner ? (
                      <CircularProgress size={28} />
                    ) : (
                      <>
                        <CloudUploadOutlinedIcon
                          color="primary"
                          sx={{ fontSize: 28 }}
                        />
                        <Typography
                          variant="caption"
                          color="primary"
                          fontWeight={500}
                        >
                          Upload Image
                        </Typography>
                      </>
                    )}

                    <input
                      hidden
                      ref={ownerImgRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        e.target.files &&
                        uploadFile(
                          e.target.files[0],
                          "owner_image_url",
                          "owner",
                          setFieldValue,
                        )
                      }
                    />
                  </Box>
                ) : (
                  <Box position="relative" width="fit-content" mx="auto">
                    <IconButton
                      size="small"
                      onClick={() => setFieldValue("owner_image_url", "")}
                      sx={{
                        position: "absolute",
                        top: -6,
                        right: -6,
                        backgroundColor: "#fff",
                        boxShadow: 1,
                        "&:hover": { backgroundColor: "#fff" },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>

                    <Avatar
                      src={values.owner_image_url}
                      sx={{
                        width: 120,
                        height: 120,
                        border: "1px solid #e0e0e0",
                      }}
                    />
                  </Box>
                )}
              </Box>

              <Stack spacing={2}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <TextField
                    label="Owner Name"
                    value={values.owner_name}
                    size="small"
                    onChange={(e) =>
                      setFieldValue("owner_name", e.target.value)
                    }
                    error={Boolean(touched.owner_name && errors.owner_name)}
                    helperText={touched.owner_name && errors.owner_name}
                    fullWidth
                  />

                  <TextField
                    label="Phone Number"
                    value={values.phone_number}
                    size="small"
                    onChange={(e) =>
                      setFieldValue("phone_number", e.target.value)
                    }
                    error={Boolean(touched.phone_number && errors.phone_number)}
                    helperText={touched.phone_number && errors.phone_number}
                    fullWidth
                  />
                </Stack>

                <Box>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems="flex-start"
                  >
                    <TextField
                      label="Aadhaar Number"
                      size="small"
                      value={values.aadhaar_no}
                      onChange={(e) =>
                        setFieldValue("aadhaar_no", e.target.value)
                      }
                      error={Boolean(touched.aadhaar_no && errors.aadhaar_no)}
                      helperText={touched.aadhaar_no && errors.aadhaar_no}
                      fullWidth
                    />

                    <Box sx={{ minWidth: 160 }}>
                      {!values.aadhaar_url ? (
                        <LoadingButton
                          fullWidth
                          size="small"
                          loading={uploading.aadhaar}
                          variant="outlined"
                          onClick={() => aadhaarRef.current?.click()}
                          sx={{
                            height: 40,
                            mt:
                              touched.aadhaar_no && errors.aadhaar_no
                                ? "22px"
                                : "0px",
                          }}
                        >
                          Upload Aadhaar
                        </LoadingButton>
                      ) : (
                        <LoadingButton
                          fullWidth
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => setFieldValue("aadhaar_url", "")}
                          sx={{
                            height: 40,
                            mt:
                              touched.aadhaar_no && errors.aadhaar_no
                                ? "22px"
                                : "0px",
                          }}
                        >
                          Remove
                        </LoadingButton>
                      )}
                    </Box>

                    <input
                      hidden
                      ref={aadhaarRef}
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) =>
                        e.target.files &&
                        uploadFile(
                          e.target.files[0],
                          "aadhaar_url",
                          "aadhaar",
                          setFieldValue,
                        )
                      }
                    />
                  </Stack>

                  {values.aadhaar_url && (
                    <Typography
                      variant="body2"
                      color="primary"
                      mt={0.75}
                      sx={{
                        wordBreak: "break-all",
                        fontSize: 12,
                      }}
                    >
                      {values.aadhaar_url}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Box display="flex" alignItems="flex-start" gap={2}>
                    <TextField
                      label="PAN Number"
                      value={values.pan_no}
                      size="small"
                      onChange={(e) =>
                        setFieldValue("pan_no", e.target.value.toUpperCase())
                      }
                      error={Boolean(touched.pan_no && errors.pan_no)}
                      helperText={touched.pan_no && errors.pan_no}
                      fullWidth
                    />

                    {!values.pan_url ? (
                      <LoadingButton
                        size="small"
                        loading={uploading.pan}
                        variant="outlined"
                        onClick={() => panRef.current?.click()}
                        sx={{
                          height: 40,
                          minWidth: 140,
                          mt: touched.pan_no && errors.pan_no ? "22px" : "0px",
                        }}
                      >
                        Upload PAN
                      </LoadingButton>
                    ) : (
                      <LoadingButton
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => setFieldValue("pan_url", "")}
                        sx={{
                          height: 40,
                          minWidth: 140,
                          mt: touched.pan_no && errors.pan_no ? "22px" : "0px",
                        }}
                      >
                        Remove
                      </LoadingButton>
                    )}

                    <input
                      hidden
                      ref={panRef}
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) =>
                        e.target.files &&
                        uploadFile(
                          e.target.files[0],
                          "pan_url",
                          "pan",
                          setFieldValue,
                        )
                      }
                    />
                  </Box>

                  {values.pan_url && (
                    <Typography
                      variant="body2"
                      color="primary"
                      mt={0.5}
                      sx={{ wordBreak: "break-all", fontSize: 12 }}
                    >
                      {values.pan_url}
                    </Typography>
                  )}
                </Box>
              </Stack>

              <Box textAlign="center" mt={4}>
                <LoadingButton
                  loading={loading}
                  variant="contained"
                  type="submit"
                >
                  {isEdit ? "Update" : "Add"}
                </LoadingButton>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default EditOwnerDetailsModal;
